import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertBanner } from '@/components/campus';
import { Loader2 } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn(email, password);
      
      console.log('Login result:', result);
      
      // Determine redirect based on user data
      if (from) {
        navigate(from, { replace: true });
      } else if (result.profile?.platform_role) {
        // Platform owner/admin - go to platform dashboard
        console.log('Redirecting to /platform (platform role)');
        navigate('/platform', { replace: true });
      } else if (result.organization && result.orgRole) {
        // Has organization - go to command center
        console.log('Redirecting to /app/command-center (has org:', result.organization?.name, 'role:', result.orgRole, ')');
        navigate('/app/command-center', { replace: true });
      } else if (result.profile && !result.organization) {
        // Has profile but no org - student goes to discover, others to onboarding
        console.log('Redirecting to /discover (no org)');
        navigate('/discover', { replace: true });
      } else {
        // Default to discover
        console.log('Redirecting to /discover (default)');
        navigate('/discover', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
          Welcome back
        </h1>
        <p className="text-soft-silver text-sm mt-2">
          Sign in to your account
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <AlertBanner 
            variant="error" 
            message={error} 
            dismissible 
            onDismiss={() => setError('')}
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-soft-silver">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@university.edu"
            className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
            data-testid="login-email"
            required
            disabled={isLoading}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="text-soft-silver">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
            data-testid="login-password"
            required
            disabled={isLoading}
          />
        </div>
        
        <div className="flex justify-end">
          <Link 
            to="/forgot-password" 
            className="text-sm text-electric-ice-blue hover:underline"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </Link>
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium"
          data-testid="login-submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>
      
      <p className="text-center text-sm text-soft-silver mt-6">
        Don't have an account?{' '}
        <Link 
          to="/signup" 
          className="text-electric-ice-blue hover:underline"
          data-testid="signup-link"
        >
          Sign up
        </Link>
      </p>
    </div>
  );
};

export default Login;
